import * as THREE from 'three';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { Renderer } from '@/renderer/Renderer';

/**
 * CUSTOM SCENE-1 MATERIAL SET (2026-07-20) — the second, sunlit look for the hero scene,
 * matching the user's reference render: terracotta brick voussoirs, cream travertine trim,
 * warm beige stage blocks, wooden doors with dark iron hardware, all lit by a warm
 * directional sun from the right with real shadows.
 *
 * Lives ALONGSIDE the baked atlases: {@link apply} saves each mesh's baked material and swaps
 * in the custom set (plus the sun + fill lights and shadow flags); {@link restore} puts
 * everything back byte-for-byte. The debug checkbox flips between the two live.
 *
 * The baked pipeline is unlit (`MeshBasicMaterial`), so this is the only place in the app
 * where real lights and `renderer.shadowMap` are switched on — and both are torn back down on
 * restore, so the baked look never pays for them.
 */
export class CustomScene1 {
  private readonly saved = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
  private readonly shadowFlags = new Map<THREE.Mesh, { cast: boolean; receive: boolean }>();
  private readonly meshes: THREE.Mesh[] = [];
  private readonly materials: THREE.MeshStandardMaterial[] = [];

  private readonly sun: THREE.DirectionalLight;
  private readonly fill: THREE.HemisphereLight;
  private readonly lightGroup = new THREE.Group();
  /** World-space centre of the scene-1 meshes — the sun orbits/aims here. */
  private readonly focus = new THREE.Vector3();
  private radius = 20;

  private prevShadowMapEnabled = false;
  private applied = false;

  private readonly log = createLogger('custom-s1');

  constructor(
    private readonly renderer: Renderer,
    private readonly scene: THREE.Scene,
  ) {
    const cfg = settings.customScene1.sun;
    this.sun = new THREE.DirectionalLight(new THREE.Color(cfg.color), cfg.intensity);
    this.sun.castShadow = cfg.shadows;
    this.sun.shadow.mapSize.set(cfg.shadowMapSize, cfg.shadowMapSize);
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.02;
    this.fill = new THREE.HemisphereLight(
      new THREE.Color(cfg.fillSky),
      new THREE.Color(cfg.fillGround),
      cfg.fillIntensity,
    );
    this.lightGroup.name = 'custom-scene1-lights';
    this.lightGroup.add(this.sun, this.sun.target, this.fill);
  }

  /**
   * Classify + register every scene-1 mesh once, from `rawName` (Blender's original name —
   * GLTFLoader strips the `.NNN` duplicate dots). Families per the user's annotated frames.
   * Returns the material for a name, or null for meshes this system must not touch
   * (`_E` emissive twins, water, the portal `Plane`, the scene-2 staircase).
   */
  register(root: THREE.Object3D): void {
    const c = settings.customScene1.colors;
    const r = settings.customScene1.roughness;

    const loader = new THREE.TextureLoader();
    const pillarTex = loader.load('/textures/custom_pillar.png');
    pillarTex.wrapS = THREE.RepeatWrapping;
    pillarTex.wrapT = THREE.RepeatWrapping;
    pillarTex.repeat.set(settings.customTextures.pillar.repeatX, settings.customTextures.pillar.repeatY);
    pillarTex.colorSpace = THREE.SRGBColorSpace;

    const stoneTex = loader.load('/textures/custom_stone.png');
    stoneTex.wrapS = THREE.RepeatWrapping;
    stoneTex.wrapT = THREE.RepeatWrapping;
    stoneTex.repeat.set(settings.customTextures.stone.repeatX, settings.customTextures.stone.repeatY);
    stoneTex.colorSpace = THREE.SRGBColorSpace;
    
    const doorTex = loader.load('/textures/custom_door.png');
    doorTex.wrapS = THREE.RepeatWrapping;
    doorTex.wrapT = THREE.RepeatWrapping;
    doorTex.repeat.set(settings.customTextures.door.repeatX, settings.customTextures.door.repeatY);
    doorTex.colorSpace = THREE.SRGBColorSpace;

    // Use HSL from settings for the stone color
    const stoneColor = new THREE.Color().setHSL(
      settings.customTextures.stone.hue,
      settings.customTextures.stone.saturation,
      settings.customTextures.stone.lightness
    );

    const brickBase = stoneColor;
    const mats = {
      trim: this.std('trim', { color: c.trim, roughness: r.trim, map: pillarTex }),
      stage: this.std('stage', { color: `#${stoneColor.getHexString()}`, roughness: r.stage, map: stoneTex }),
      wood: this.std('wood', { color: c.wood, roughness: r.wood, map: doorTex }),
      iron: this.std('iron', { color: c.iron, roughness: r.iron, metalness: 0.75 }),
    };

    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const raw = ((mesh.userData.rawName as string | undefined) ?? mesh.name).replace(/\.\d+$/, '');
      
      // Fix UVs to ensure they use the correct set for tiled textures
      const uv0 = mesh.geometry.getAttribute('uv');
      if (uv0) mesh.geometry.setAttribute('uv', uv0);

      let material: THREE.MeshStandardMaterial | null = null;
      if (/^\d+$/.test(raw)) {
        // Numbered voussoir → its own terracotta tone (the reference bricks visibly vary).
        // Deterministic per stone number, so the look is stable across reloads.
        const n = Number(raw);
        const jitter = settings.customScene1.colors.brickJitter;
        const t = fract(Math.sin(n * 127.1) * 43758.5453); // hash: stable pseudo-random 0..1
        const tone = brickBase
          .clone()
          .offsetHSL((t - 0.5) * 0.035, (t - 0.5) * jitter * 0.5, (fract(t * 7.13) - 0.5) * jitter);
        material = this.std('brick', { color: `#${tone.getHexString()}`, roughness: r.brick, map: stoneTex });
      } else if (raw === 'Door_Main_Pillar_S_1' || raw.startsWith('Pillar_') || raw === 'Stone_Pillar' || raw === 'Stage_Pillar_S_1' || raw === 'Pillar_Base_S_1' || raw === 'Pillar_base_top_S_1') {
        material = mats.trim;
      } else if (raw === 'Stage_Stone_S_1' || /^\d+(_E)?$/i.test(raw)) {
        material = mats.stage;
      } else if (raw === 'Left_Door' || raw === 'Right_Door') {
        material = mats.wood;
      } else if (raw.startsWith('Medieval Double Door')) {
        material = mats.iron;
      }
      if (!material) return;

      this.meshes.push(mesh);
      this.saved.set(mesh, mesh.material);
      mesh.userData.customS1Material = material;
    });

    // Sun aim + shadow frustum sized to the actual scene-1 bounds.
    const box = new THREE.Box3();
    for (const mesh of this.meshes) box.expandByObject(mesh);
    box.getCenter(this.focus);
    this.radius = Math.max(box.getSize(new THREE.Vector3()).length() * 0.55, 10);
    const cam = this.sun.shadow.camera;
    cam.left = -this.radius;
    cam.right = this.radius;
    cam.top = this.radius;
    cam.bottom = -this.radius;
    cam.near = 0.5;
    cam.far = this.radius * 4;
    cam.updateProjectionMatrix();
    this.sun.target.position.copy(this.focus);

    this.log.info(`registered ${this.meshes.length} scene-1 mesh(es) for the custom sunlit look.`);
  }

  /** Swap the custom set in (idempotent). */
  apply(): void {
    if (this.applied || !this.meshes.length) return;
    this.applied = true;

    for (const mesh of this.meshes) {
      mesh.material = mesh.userData.customS1Material as THREE.Material;
      this.shadowFlags.set(mesh, { cast: mesh.castShadow, receive: mesh.receiveShadow });
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }

    this.applySunSettings();
    this.scene.add(this.lightGroup);

    // The baked pipeline never uses the shadow map — turn it on only while this look is active.
    this.prevShadowMapEnabled = this.renderer.instance.shadowMap.enabled;
    this.renderer.instance.shadowMap.enabled = settings.customScene1.sun.shadows;
    this.renderer.instance.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.instance.shadowMap.needsUpdate = true;
  }

  /** Put the baked materials (and renderer state) back exactly as they were. */
  restore(): void {
    if (!this.applied) return;
    this.applied = false;
    for (const mesh of this.meshes) {
      const saved = this.saved.get(mesh);
      if (saved) mesh.material = saved;
      const flags = this.shadowFlags.get(mesh);
      if (flags) {
        mesh.castShadow = flags.cast;
        mesh.receiveShadow = flags.receive;
      }
    }
    this.scene.remove(this.lightGroup);
    this.renderer.instance.shadowMap.enabled = this.prevShadowMapEnabled;
  }

  setEnabled(on: boolean): void {
    if (on) this.apply();
    else this.restore();
  }

  /** Re-aim the sun + push light settings from the GUI (live). */
  applySunSettings(): void {
    const cfg = settings.customScene1.sun;
    const az = THREE.MathUtils.degToRad(cfg.azimuthDeg);
    const el = THREE.MathUtils.degToRad(cfg.elevationDeg);
    // Azimuth 0 = +Z (camera side), 90 = from the RIGHT (+X) — matching the reference's sun.
    const dist = this.radius * 2.5;
    this.sun.position.set(
      this.focus.x + Math.sin(az) * Math.cos(el) * dist,
      this.focus.y + Math.sin(el) * dist,
      this.focus.z + Math.cos(az) * Math.cos(el) * dist,
    );
    this.sun.color.set(cfg.color);
    this.sun.intensity = cfg.intensity;
    this.sun.castShadow = cfg.shadows;
    this.fill.color.set(cfg.fillSky);
    this.fill.groundColor.set(cfg.fillGround);
    this.fill.intensity = cfg.fillIntensity;
    if (this.applied) {
      this.renderer.instance.shadowMap.enabled = cfg.shadows;
      this.renderer.instance.shadowMap.needsUpdate = true;
    }
  }

  /** Push family colour/roughness edits from the GUI into the live materials. */
  applyMaterialSettings(): void {
    // Family materials are shared; per-stone bricks each own a clone, so retint those from the
    // base + their stored hash offset would drift — simplest correct behaviour for the GUI is:
    // colours other than brick apply instantly; brick colour changes need the toggle flipped
    // off/on (documented on the GUI control).
    const c = settings.customScene1.colors;
    const r = settings.customScene1.roughness;
    const t = settings.customTextures.stone;
    
    const stoneColor = new THREE.Color().setHSL(t.hue, t.saturation, t.lightness);
    
    for (const material of this.materials) {
      const kind = material.userData.kind as string | undefined;
      if (kind === 'trim') { material.roughness = r.trim; }
      if (kind === 'stage') { material.color.copy(stoneColor); material.roughness = r.stage; }
      if (kind === 'wood') { material.color.set(c.wood); material.roughness = r.wood; }
      if (kind === 'iron') { material.color.set(c.iron); material.roughness = r.iron; }
      if (kind === 'brick') {
        // We do not live-update brick colors because they are jittered on load,
        // but we can update roughness.
        material.roughness = r.brick; 
      }
    }
  }

  private std(
    kind: 'brick' | 'trim' | 'stage' | 'wood' | 'iron',
    opts: { color: string; roughness: number; metalness?: number; map?: THREE.Texture | null },
  ): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(opts.color),
      roughness: opts.roughness,
      metalness: opts.metalness ?? 0,
      map: opts.map ?? null,
    });
    material.userData.kind = kind; // read back by applyMaterialSettings
    this.materials.push(material);
    return material;
  }

  dispose(): void {
    this.restore();
    for (const material of this.materials) {
      material.map?.dispose();
      material.dispose();
    }
    this.materials.length = 0;
    this.saved.clear();
    this.meshes.length = 0;
  }
}

const fract = (v: number): number => v - Math.floor(v);


